// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LazyImageProps } from "@hardwoods/MobiusOverrides/LazyImage/LazyImage";
import { ProductImageStyles } from "@hardwoods/Overrides/Components/ProductImage";
import { ProductPriceStyles } from "@hardwoods/Overrides/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@hardwoods/Overrides/Components/SmallHeadingAndText";
import cartLineCardStyles, {
    flexBasisForFourRightItems,
    flexBasisForThreeRightItems,
} from "@hardwoods/Overrides/Widgets/Cart/Cartline.style";
import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import List from "@hardwoods/XDL/Icons/List";
import color from "@hardwoods/XDL/Utilities/color";
import { iconButtonCommonStyles, iconLinkCommonStyles } from "@hardwoods/XDL/Utilities/iconButtonCommonStyles";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import { OrderLineNotesStyles } from "@insite/content-library/Components/OrderLinesList";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardGridItem?: GridItemProps;
    cardContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: TypographyProps;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    extendedUnitNetPriceText?: TypographyProps;

    // XNG-Changes
    infoAndImageContainer?: InjectableCss;
    qtyAndPriceContainer?: InjectableCss;
    orderLineInfoContainer?: GridContainerProps;
    productPartNumbersGridItem?: GridItemProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    priceContainer?: InjectableCss;
    priceUomHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineCardActionsContainer?: InjectableCss;
    productImage?: ProductImageStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    productPrice?: ProductPriceStyles;
    totalPriceContainer?: InjectableCss;
    subtotalWithoutVatText?: TypographyPresentationProps;
    extendedUnitNetPriceGridItem?: InjectableCss;
}

const infoRightSectionHeadingAndText: SmallHeadingAndTextStyles = {
    heading: {
        size: 10,
        transform: "uppercase",
        weight: "bold",
        css: css`
            padding-left: 0;
        `,
    },
    text: {
        size: 15,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 22px;
        `,
    },
};

export const xOrderLineGenerateStyles = (numOfRightItems = 3): OrderLinesListStyles => ({
    gridContainer: {
        gap: 10,
        css: css`
            @media print {
                break-inside: avoid;
            }
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    cardContainer: {
        ...mergeWithCss(cartLineCardStyles.infoSection, {
            css: css`
                border-bottom: 1px solid ${color("common.border")};
                padding: 20px 0;
            `,
        }),
    },
    infoAndImageContainer: {
        ...mergeWithCss(cartLineCardStyles.infoAndImageContainer, {
            css: css`
                ${numOfRightItems === 4 &&
                css`
                    flex: 1 0 ${flexBasisForFourRightItems.infoAndImageContainer};
                `}
                ${numOfRightItems === 3 &&
                css`
                    flex: 1 0 ${flexBasisForThreeRightItems.infoAndImageContainer};
                `}
            `,
        }),
    },
    productImageLink: {
        ...cartLineCardStyles.productImage?.linkWrappingImage,
    },
    orderLineCardImage: {
        ...cartLineCardStyles.productImage?.image,
    },
    orderLineInfoContainer: {
        ...cartLineCardStyles.orderLineInfoContainer,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            ${cartLineCardStyles.productBrandAndDescriptionGridItem?.css}
        `,
    },
    erpNumberHeadingAndText: {
        ...cartLineCardStyles.partNumbersHeadingAndTextStyles,
    },
    manufacturerHeadingAndText: {
        ...cartLineCardStyles.partNumbersHeadingAndTextStyles,
    },
    customerProductNumberHeadingAndText: {
        ...cartLineCardStyles.partNumbersHeadingAndTextStyles,
    },
    productDescriptionStyles: {
        ...cartLineCardStyles.productDescription,
    },
    productBrandStyles: {
        ...cartLineCardStyles.productBrand,
    },
    productInfoSectionOptionsText: {
        ...cartLineCardStyles.productInfoSectionOptionsText,
    },
    productInfoSectionOptionsGridItem: {
        css: css`
            display: flex;
            flex-direction: columns;
        `,
    },
    orderLineInfoPromotionText: {
        ...cartLineCardStyles.productInfoSectionOptionsText,
    },
    orderLineNotes: {
        typographyStyles: {
            ...cartLineCardStyles.noteText,
        },
    },
    qtyAndPriceContainer: {
        ...mergeWithCss(cartLineCardStyles.qtyAndPriceContainer, {
            css: css`
                ${numOfRightItems === 4 &&
                css`
                    flex: 1 0 ${flexBasisForFourRightItems.qtyAndPriceContainer};
                    > div {
                        flex: 1 1 ${flexBasisForFourRightItems.qtyAndPriceItems};
                    }
                `}
                ${numOfRightItems === 3 &&
                css`
                    flex: 1 0 ${flexBasisForThreeRightItems.qtyAndPriceContainer};
                    > div {
                        flex: 1 1 ${flexBasisForThreeRightItems.qtyAndPriceItems};
                    }
                `}

                > div {
                    ${TypographyStyle}[data-test-selector="heading"] {
                        display: none;
                        @media print {
                            display: block;
                        }
                    }
                }
                ${xScreen.lte(cartLineBreakpoints.md)} {
                    > div {
                        min-width: 150px;
                        flex: 1 1 0;
                        &:last-child {
                            min-width: 180px;
                        }
                        &.multiplePrice {
                            // price
                            flex: 2 1 auto;
                        }
                        ${TypographyStyle}[data-test-selector="heading"] {
                            display: block;
                        }
                    }
                }
            `,
        }),
    },
    priceContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            > ${TypographyStyle}, > div,
            > section {
                margin-bottom: 8px;
            }
            ${xScreen.lte(cartLineBreakpoints.md)} {
                flex-direction: row;
                flex-wrap: wrap;
                justify-content: flex-start;
                > div {
                    width: calc(50% - 10px);
                    min-width: 160px;
                    margin-right: 10px;
                    margin-bottom: 10px;
                }
            }
        `,
    },
    priceUomHeadingAndText: {
        heading: {
            size: 10,
            transform: "uppercase",
            weight: "bold",
            css: css`
                padding-left: 0;
            `,
        },
        text: {
            size: 16,
            weight: "bold",
            css: css`
                .unitOfMeasure {
                    font-size: 14px;
                    font-weight: bold;
                }
            `,
        },
    },
    priceHeadingAndText: {
        ...mergeWithCss(infoRightSectionHeadingAndText, {
            text: {
                css: css`
                    .unitOfMeasure {
                        font-size: 12px;
                        font-weight: bold;
                        letter-spacing: 0;
                        line-height: 14px;
                        white-space: nowrap;
                    }
                `,
            },
        }),
    },
    productInfoQtyOrderedHeadingAndText: {
        ...infoRightSectionHeadingAndText,
    },
    productInfoQtyShippedHeadingAndText: {
        ...infoRightSectionHeadingAndText,
    },
    subtotalWithoutVatHeadingAndText: {
        ...mergeWithCss(infoRightSectionHeadingAndText, {
            text: {
                size: 20,
            },
        }),
    },
    productInfoSubtotalHeadingAndText: {
        ...mergeWithCss(infoRightSectionHeadingAndText, {
            text: {
                size: 20,
            },
        }),
    },
    orderLineCardAddToListButton: {
        ...iconButtonCommonStyles(List, {
            variantType: "secondary",
            sizeVariant: "small",
            css: css`
                padding: 0;
            `,
        }),
    },
    orderLineCardActionsContainer: {
        css: css`
            display: flex;
            margin-top: auto;
        `,
    },
    productPartNumbers: {
        ...cartLineCardStyles.productPartNumbers,
    },
    productImage: {
        ...cartLineCardStyles.productImage,
    },
    productPrice: {
        ...cartLineCardStyles.productPrice,
    },
    extendedUnitNetPriceText: {
        ...cartLineCardStyles.extendedUnitNetPriceText,
    },
    totalPriceContainer: {
        ...cartLineCardStyles.totalPriceContainer,
    },
    extendedUnitNetPriceGridItem: {
        ...cartLineCardStyles.extendedUnitNetPriceGridItem,
    },
    subtotalWithoutVatText: {
        ...cartLineCardStyles.subtotalWithoutVatText,
    },
});
